/* Theme-only presentation adapter. Shopify Forms retains all signup/discount logic. */
(() => {
  const config = document.currentScript;
  const cssUrl = config.dataset.stylesheet;
  const label = config.dataset.label;
  const mobileLabel = config.dataset.mobileLabel || label;
  const mobileHeadline = config.dataset.mobileHeadline || mobileLabel;
  const mobile = matchMedia('(max-width: 749px)');
  const eyebrow = config.dataset.eyebrow;
  const accessibleLabel = config.dataset.accessibleLabel;
  const mounted = new WeakSet();
  const hosts = new Set();
  let pending = false;
  let previewBar;
  let scrollLock;
  const restoreScroll = () => {
    if (!scrollLock) return;
    const saved = scrollLock;
    scrollLock = undefined;
    Object.entries(saved.body).forEach(([property, value]) => {
      document.body.style[property] = value;
    });
    window.scrollTo({ left: saved.x, top: saved.y, behavior: 'instant' });
  };
  const syncScrollLock = active => {
    if (!active) { restoreScroll(); return; }
    if (scrollLock) return;
    const body = document.body.style;
    scrollLock = {
      x: window.scrollX, y: window.scrollY,
      body: Object.fromEntries(['position', 'top', 'left', 'width', 'overflow'].map(property => [property, body[property]]))
    };
    body.position = 'fixed';
    body.top = `${-scrollLock.y}px`;
    body.left = `${-scrollLock.x}px`;
    body.width = '100%';
    body.overflow = 'hidden';
  };
  const previewResize = new ResizeObserver(() => schedule());
  const overlaySelector = [
    'quick-add-modal[open]', 'modal-dialog[open]', 'cart-drawer.active', '.cart-notification.active',
    '#Details-menu-drawer-container[open]', 'details-modal details[open]',
    'menu-drawer > details[open]', '.mobile-facets__disclosure[open]',
    '.section-header .disclosure__list-wrapper:not([hidden])[aria-hidden="false"]'
  ].join(',');
  const sync = () => {
    pending = false;
    // The modern Shopify preview iframe spans the viewport; its wrapper is
    // the actual occupied bottom strip. Measure that instead of iframe height.
    const nextPreviewBar = document.getElementById('PBarNextFrameWrapper');
    if (nextPreviewBar !== previewBar) {
      previewResize.disconnect();
      previewBar = nextPreviewBar;
      if (previewBar) previewResize.observe(previewBar);
    }
    const previewRect = previewBar?.getBoundingClientRect();
    const previewInset = previewRect?.width && previewRect.height && previewRect.bottom >= innerHeight - 1
      ? Math.max(0, innerHeight - previewRect.top) : 0;
    const suspended = !!document.querySelector(overlaySelector) || (mobile.matches && previewInset > innerHeight / 2);
    document.querySelectorAll('shopify-forms-embed#app-embed-container-1060237').forEach(host => {
      if (!host.shadowRoot) return;
      if (!mounted.has(host)) {
        const style = document.createElement('link');
        style.rel = 'stylesheet';
        style.href = cssUrl;
        host.shadowRoot.append(style);
        host.setAttribute('data-aveyra-newsletter-widget', '');
        mounted.add(host);
        hosts.add(host);
        new MutationObserver(schedule).observe(host.shadowRoot, {
          childList: true, subtree: true, attributes: true,
          attributeFilter: ['data-current-step', 'class']
        });
      }
    });
    let mobileDialogOpen = false;
    hosts.forEach(host => {
      if (!host.isConnected) { hosts.delete(host); return; }
      const teaser = host.shadowRoot.querySelector('[data-testid="form-teaser"]');
      const heading = teaser?.querySelector('h3');
      // A dismissed embed can have neither a teaser nor a dialog.
      const dialog = host.shadowRoot.querySelector('[data-sizing="form-wrapper"][role="dialog"]');
      const formOpen = !!dialog;
      if (mobile.matches && formOpen && !suspended) mobileDialogOpen = true;
      host.style.setProperty('--aveyra-preview-bottom-inset', `${previewInset}px`);
      if (heading) {
        if (!heading.querySelector('[data-aveyra-teaser-copy]')) {
          const caption = document.createElement('span');
          caption.className = 'aveyra-teaser__eyebrow';
          caption.textContent = eyebrow;
          const copy = document.createElement('span');
          copy.setAttribute('data-aveyra-teaser-copy', '');
          copy.className = 'aveyra-teaser__offer';
          copy.textContent = mobileHeadline;
          const supporting = document.createElement('span');
          supporting.className = 'aveyra-teaser__supporting';
          supporting.textContent = config.dataset.mobileSupporting;
          // The teaser is already Shopify's native button. Keep its click
          // handler and keyboard behavior instead of nesting another button.
          const cta = document.createElement('span');
          cta.className = 'aveyra-teaser__cta';
          cta.textContent = config.dataset.mobileCta;
          heading.replaceChildren(caption, copy, supporting, cta);
        }
        const copy = heading.querySelector('[data-aveyra-teaser-copy]');
        const nextLabel = mobileHeadline;
        if (copy.textContent !== nextLabel) copy.textContent = nextLabel;
      }
      if (teaser) teaser.setAttribute('aria-label', accessibleLabel);
      host.toggleAttribute('data-aveyra-form-open', formOpen);
      host.toggleAttribute('data-aveyra-suspended', suspended);
      host.inert = suspended;
    });
    syncScrollLock(mobileDialogOpen);
  };
  function schedule() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(sync);
  }
  new MutationObserver(schedule).observe(document.body, {
    childList: true, subtree: true, attributes: true,
    attributeFilter: ['open', 'class', 'aria-hidden', 'aria-expanded']
  });
  // The app may attach its shadow root after inserting the host.
  let attempts = 0;
  const discover = () => {
    schedule();
    if (!hosts.size && ++attempts < 60) setTimeout(discover, 250);
  };
  discover();
  window.addEventListener('pageshow', schedule);
  window.addEventListener('resize', schedule);
  window.addEventListener('pagehide', restoreScroll);
  mobile.addEventListener('change', schedule);
})();
